/**
 * ChatMarkdownRenderer Component
 * AIDLC Administrative Backend
 *
 * Renders markdown content in chat messages with elegant, crisp styling.
 * Handles code blocks, inline code, headings, lists, links, blockquotes,
 * tables, bold, italic — all themed to LDAJA brand.
 *
 * Designed by Maya (UI/UX) for the chat interface.
 */

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface ChatMarkdownRendererProps {
  content: string;
  isUser?: boolean;
}

const agentComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-gray-900 mt-3 mb-1.5 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-gray-900 mt-2.5 mb-1 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-gray-800 mt-2 mb-1 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-xs font-semibold text-gray-700 mt-1.5 mb-0.5 first:mt-0">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-sm leading-relaxed mb-1.5 last:mb-0">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-gray-600">{children}</em>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-ldaja-blue hover:underline font-medium"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ml-4 mb-1.5 space-y-0.5 text-sm">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-4 mb-1.5 space-y-0.5 text-sm">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-ldaja-blue/40 pl-3 my-1.5 text-gray-600 italic text-sm">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      const lang = className?.replace('language-', '') || '';
      return (
        <div className="my-2 rounded-md overflow-hidden bg-gray-900 border border-gray-700">
          {lang && (
            <div className="px-3 py-1 bg-gray-800 border-b border-gray-700">
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">{lang}</span>
            </div>
          )}
          <pre className="px-3 py-2 overflow-x-auto">
            <code className="text-xs font-mono text-gray-200 leading-5">{children}</code>
          </pre>
        </div>
      );
    }
    return (
      <code className="px-1 py-0.5 bg-gray-100 border border-gray-200 rounded text-xs font-mono text-ldaja-blue">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  hr: () => <hr className="my-2 border-gray-200" />,
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-md border border-gray-200">
      <table className="w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gray-50 border-b border-gray-200">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-gray-100 last:border-0">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 text-left font-semibold text-gray-600">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 text-gray-700">{children}</td>
  ),
};

const userComponents: Components = {
  p: ({ children }) => (
    <p className="text-sm leading-relaxed mb-1.5 last:mb-0">{children}</p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <div className="my-2 rounded-md overflow-hidden bg-blue-800/50 border border-blue-400/30">
          <pre className="px-3 py-2 overflow-x-auto">
            <code className="text-xs font-mono text-blue-100 leading-5">{children}</code>
          </pre>
        </div>
      );
    }
    return (
      <code className="px-1 py-0.5 bg-blue-500/40 rounded text-xs font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <>{children}</>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline font-medium text-white"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ml-4 mb-1.5 space-y-0.5 text-sm">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-4 mb-1.5 space-y-0.5 text-sm">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="leading-relaxed">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-white/40 pl-3 my-1.5 opacity-80 italic text-sm">
      {children}
    </blockquote>
  ),
};

export function ChatMarkdownRenderer({ content, isUser = false }: ChatMarkdownRendererProps) {
  // Plain short messages without markdown syntax — render as simple text for speed
  if (!content.includes('```') && !content.includes('**') && !content.includes('# ')
      && !content.includes('- ') && !content.includes('1. ') && !content.includes('|')
      && !content.includes('> ') && !content.includes('[') && !content.includes('`')) {
    return <p className="text-sm leading-relaxed whitespace-pre-wrap">{content}</p>;
  }

  return (
    <div className="markdown-chat">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={isUser ? userComponents : agentComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
